import { Box, Link as LinkMUI, useTheme } from '@mui/material';

import { Text } from 'components';
import { formatDate, formatPrice } from 'core/shared';
import { useHasMobileView } from 'hooks/useHasMobileView';
import { replaceText, useTranslations } from 'hooks/useTranslations';

import { UseQuery } from 'useQuery';
import { InvoiceLine } from 'useQuery/types';

import { formatPeriod, formatSignedPrice } from './InvoicingShared';

interface OrganisationNextInvoicePreviewProps {
  organisationId: string;
  onViewAllInvoices: () => void;
}

// Shows only the next invoice in full line detail. Reflects SAVED state only:
// the next-invoice query is refetched after each section save.
function OrganisationNextInvoicePreview({
  organisationId,
  onViewAllInvoices,
}: OrganisationNextInvoicePreviewProps) {
  const theme = useTheme();
  const { hasMobileView } = useHasMobileView();
  const translations = useTranslations('organisationInvoicing');

  const { data: nextInvoiceData } = UseQuery.nextInvoice({ id: organisationId });

  const invoice = nextInvoiceData?.next_invoice ?? null;
  const earlierCount = nextInvoiceData?.earlier_invoice_count ?? 0;
  const scheduledCount = nextInvoiceData?.scheduled_invoices?.length ?? 0;

  function Line({ line }: { line: InvoiceLine }) {
    const negative = line.amount_net < 0;
    const period = formatPeriod(line.period_start, line.period_end);
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
          paddingY: 0.75,
        }}
      >
        <Box>
          <Text>{line.description}</Text>
          {!line.counts_as_revenue && (
            <Text.Small color="gray">{translations.exclRevenueTag}</Text.Small>
          )}
          {!!period && (
            <Text variant="caption" color="gray">
              {period}
            </Text>
          )}
        </Box>
        <Text color={negative ? 'success.main' : undefined} noWrap>
          {formatSignedPrice(line.amount_net)}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        padding: 2,
        marginBottom: 2,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        maxWidth: `calc(100vw - ${theme.spacing(hasMobileView ? 4 : 6)})`,
      }}
    >
      <Text.Bold>{translations.previewTitle}</Text.Bold>

      {earlierCount > 0 && (
        <Box sx={{ paddingTop: 1 }}>
          <Text.Small color="gray">
            {earlierCount === 1
              ? translations.earlierInvoiceOne
              : replaceText({
                  text: translations.earlierInvoicesMany,
                  replaceValues: [earlierCount],
                })}
          </Text.Small>
        </Box>
      )}

      {!invoice && (
        <Box sx={{ paddingY: 2 }}>
          <Text color="gray">{translations.noUpcomingInvoices}</Text>
        </Box>
      )}

      {!!invoice && (
        <>
          <Box sx={{ paddingTop: 2, borderTop: 1, borderColor: 'divider', marginTop: 2 }}>
            <Text.Bold>{formatPeriod(invoice.period_start, invoice.period_end)}</Text.Bold>
            <Text.Small color="gray">
              {replaceText({
                text: translations.invoicedOnDate,
                replaceValues: [
                  formatDate({ date: invoice.invoice_date, variant: 'dayMonthYear' }),
                ],
              })}
            </Text.Small>
          </Box>

          <Box sx={{ paddingTop: 1 }}>
            {invoice.lines.map((line, index) => (
              <Line key={line.item_id ?? `${line.category}-${index}`} line={line} />
            ))}
          </Box>

          <Box sx={{ paddingTop: 1, borderTop: 1, borderColor: 'divider', marginTop: 1 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                paddingY: 0.75,
              }}
            >
              <Text.Bold>{translations.total}</Text.Bold>
              <Text.Bold>{formatPrice({ price: invoice.net_total, symbol: true })}</Text.Bold>
            </Box>
          </Box>
        </>
      )}

      {scheduledCount > 1 && (
        <Box sx={{ paddingTop: 1.5 }}>
          <LinkMUI component="button" type="button" onClick={onViewAllInvoices} underline="hover">
            {replaceText({
              text: translations.viewAllScheduled,
              replaceValues: [scheduledCount],
            })}
          </LinkMUI>
        </Box>
      )}

      <Box sx={{ paddingTop: 1.5 }}>
        <Text.Small color="gray">
          {`${translations.amountsExclVat} ${translations.commissionsVariableNote}`}
        </Text.Small>
      </Box>
    </Box>
  );
}

export { OrganisationNextInvoicePreview };
